import { ArrowRight, TrendingUp, Users, Award } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function Home() {
  const stats = [
    { value: '$150M+', label: 'Portfolio Value' },
    { value: '30+', label: 'Years Experience' },
    { value: '200+', label: 'Successful Projects' }
  ];

  const services = [
    {
      title: 'Real Estate Consulting',
      description: 'Expert guidance on property investments and market analysis',
      icon: <TrendingUp size={32} />
    },
    {
      title: 'Project Development',
      description: 'Full-service development from concept to completion',
      icon: <Award size={32} />
    },
    {
      title: 'Investor Linking',
      description: 'Connect with strategic partners for complex funding needs',
      icon: <Users size={32} />
    }
  ];

  const testimonials = [
    {
      quote: "Working with Jr Morales Investment Group transformed our development vision into reality. Their expertise and network are unmatched.",
      author: "Michael Chen",
      company: "Pacific Development Corp"
    },
    {
      quote: "The team's 30+ years of experience showed in every aspect of our project. They navigated complex funding with ease.",
      author: "Sarah Johnson",
      company: "Urban Properties LLC"
    },
    {
      quote: "From initial consultation to project completion, Jr Morales Investment Group exceeded all expectations. True professionals.",
      author: "David Martinez",
      company: "Martinez Real Estate Group"
    }
  ];

  const featuredProjects = [
    {
      title: 'Marina Bay Mixed-Use',
      value: '$45M',
      location: 'San Francisco, CA',
      image: 'https://images.unsplash.com/photo-1738627209964-764a44aeee9e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBza3lzY3JhcGVyJTIwc3Vuc2V0fGVufDB8fHx8MTc2NTE1NDQyNnww&ixlib=rb-4.1.0&q=85'
    },
    {
      title: 'Downtown Tower',
      value: '$62M',
      location: 'Los Angeles, CA',
      image: 'https://images.unsplash.com/photo-1764603852093-6e4918c3197a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBza3lzY3JhcGVyJTIwc3Vuc2V0fGVufDB8fHx8MTc2NTE1NDQyNnww&ixlib=rb-4.1.0&q=85'
    },
    {
      title: 'Luxury Residential',
      value: '$28M',
      location: 'Beverly Hills, CA',
      image: 'https://images.unsplash.com/photo-1751698186759-5bac83376c9f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBza3lzY3JhcGVyJTIwc3Vuc2V0fGVufDB8fHx8MTc2NTE1NDQyNnww&ixlib=rb-4.1.0&q=85'
    }
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      <section className="relative h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1751698186759-5bac83376c9f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBza3lzY3JhcGVyJTIwc3Vuc2V0fGVufDB8fHx8MTc2NTE1NDQyNnww&ixlib=rb-4.1.0&q=85"
            alt="Luxury architecture"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h1 className="text-5xl md:text-7xl font-medium tracking-tight leading-none text-[#f8fafc] mb-6" data-testid="hero-title">
            Wealth Built on<br />
            <span className="text-[#d4af37]">Solid Ground</span>
          </h1>
          <p className="text-lg md:text-xl leading-relaxed text-slate-300 mb-12 max-w-2xl mx-auto">
            Over 30 years of expertise in real estate investment and development. We transform visions into landmark projects.
          </p>
          <Link to="/contact">
            <Button className="bg-[#d4af37] text-[#020617] hover:bg-[#b5952f] rounded-sm px-8 py-6 text-lg font-medium transition-all duration-300 hover:scale-105" data-testid="hero-cta-button">
              Schedule Consultation <ArrowRight className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <section className="py-24 md:py-32 glass-morphism" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {stats.map((stat, index) => (
              <div key={index} className="text-center" data-testid={`stat-item-${index}`}>
                <div className="text-5xl md:text-6xl font-medium text-[#d4af37] mb-3">
                  {stat.value}
                </div>
                <div className="text-sm tracking-widest uppercase text-slate-400">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#020617]" data-testid="services-preview-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-normal tracking-tight text-[#f8fafc] mb-6">
              Our Expertise
            </h2>
            <p className="text-lg md:text-xl leading-relaxed text-slate-300 max-w-3xl mx-auto">
              Comprehensive real estate services backed by three decades of industry leadership
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                data-testid={`service-card-${index}`}
                className="group relative overflow-hidden bg-slate-900/50 border border-white/5 p-8 hover:border-[#d4af37]/30 transition-all duration-500"
              >
                <div className="text-[#d4af37] mb-6">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-normal text-[#f8fafc] mb-4">
                  {service.title}
                </h3>
                <p className="text-base leading-relaxed text-slate-400">
                  {service.description}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button className="bg-transparent border border-[#d4af37]/30 text-[#d4af37] hover:bg-[#d4af37]/10 rounded-sm px-8 py-4 transition-all" data-testid="view-all-services-button">
                View All Services <ArrowRight className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#0f172a]" data-testid="featured-projects-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-normal tracking-tight text-[#f8fafc] mb-6">
              Featured Projects
            </h2>
            <p className="text-lg md:text-xl leading-relaxed text-slate-300 max-w-3xl mx-auto">
              A showcase of our landmark developments and successful partnerships
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredProjects.map((project, index) => (
              <div
                key={index}
                data-testid={`project-card-${index}`}
                className="group relative aspect-[4/5] overflow-hidden"
              >
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="text-[#d4af37] text-sm tracking-widest uppercase mb-2">
                    {project.value}
                  </div>
                  <h3 className="text-2xl font-normal text-[#f8fafc] mb-2">
                    {project.title}
                  </h3>
                  <p className="text-slate-300 text-sm">
                    {project.location}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/projects">
              <Button className="bg-[#d4af37] text-[#020617] hover:bg-[#b5952f] rounded-sm px-8 py-4 font-medium transition-all duration-300 hover:scale-105" data-testid="view-all-projects-button">
                View All Projects <ArrowRight className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#020617]" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-normal tracking-tight text-[#f8fafc] mb-6">
              Client Testimonials
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                data-testid={`testimonial-card-${index}`}
                className="glass-morphism p-8"
              >
                <p className="text-lg leading-relaxed text-slate-300 mb-6 italic">
                  "{testimonial.quote}"
                </p>
                <div className="border-t border-white/10 pt-6">
                  <p className="text-[#d4af37] font-medium">
                    {testimonial.author}
                  </p>
                  <p className="text-slate-400 text-sm">
                    {testimonial.company}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}